'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/nextjs';

/**
 * R2-15: is the browser's signed-in session still a real Clerk identity?
 *
 * Clerk's client state can outlive the identity: after an account is
 * purged (user deleted, sessions revoked) a tab can still read
 * `isSignedIn` with zero memberships until Clerk next refreshes. Every
 * "Create your workspace" offer would then invite a ghost to sign up
 * again. Minting a fresh session token (`skipCache`) asks Clerk's
 * frontend API directly: a revoked session or deleted user yields no
 * token (or an auth error).
 *
 * - `live`: a fresh token came back.
 * - `gone`: no token, or Clerk answered 401/403/404.
 * - `unknown`: a network-ish failure, or no `getToken` (tests, SSR). Never
 *   treated as gone, so a real first-run user is never signed out on a blip.
 */
export type SessionVerdict = 'live' | 'gone' | 'unknown';

type GetToken = (options?: { skipCache?: boolean }) => Promise<string | null>;

function authGone(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const status = (error as { status?: unknown }).status;
  if (status === 401 || status === 403 || status === 404) return true;
  const errors = (error as { errors?: unknown }).errors;
  if (Array.isArray(errors)) {
    return errors.some((row) => {
      const code = typeof row?.code === 'string' ? row.code : '';
      return /not_found|signed_out|session.*(revoked|ended|expired|invalid)|authentication_invalid/.test(code);
    });
  }
  return false;
}

export async function verifyLiveSession(getToken: GetToken | undefined): Promise<SessionVerdict> {
  if (typeof getToken !== 'function') return 'unknown';
  try {
    const token = await getToken({ skipCache: true });
    return token ? 'live' : 'gone';
  } catch (error) {
    return authGone(error) ? 'gone' : 'unknown';
  }
}

export const SIGNED_OUT_REDIRECT = '/sign-in';

/**
 * The one guard in front of every "Create your workspace" offer (the home
 * page's first-run setup and the chat's workspace gate). While `active`,
 * it checks the session once; a gone identity is signed out and sent to
 * sign-in, so a stale tab ends cleanly instead of offering to create a
 * workspace. Returns `ready` once the offer may show.
 */
export function useLiveSessionGate(active: boolean): 'checking' | 'ready' {
  const { getToken, signOut } = useAuth() as {
    getToken?: GetToken;
    signOut?: (options?: { redirectUrl?: string }) => Promise<unknown>;
  };
  const [verdict, setVerdict] = useState<SessionVerdict | null>(null);

  useEffect(() => {
    if (!active || verdict !== null) return;
    let cancelled = false;
    void verifyLiveSession(getToken).then(async (next) => {
      if (cancelled) return;
      setVerdict(next);
      if (next !== 'gone') return;
      try {
        if (typeof signOut === 'function') {
          await signOut({ redirectUrl: SIGNED_OUT_REDIRECT });
          return;
        }
      } catch {
        // Sign-out failed: a hard redirect still ends the stale page.
      }
      if (typeof window !== 'undefined') window.location.assign(SIGNED_OUT_REDIRECT);
    });
    return () => {
      cancelled = true;
    };
  }, [active, verdict, getToken, signOut]);

  return verdict === 'live' || verdict === 'unknown' ? 'ready' : 'checking';
}
